import {
  Page, Card, FormLayout, TextField, Select, Checkbox, Button,
  BlockStack, InlineStack, Text, Banner, Divider, Box,
} from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getForm, saveForm } from '../lib/api';
import { FIELD_TYPES, newField, newOption } from '../lib/fields';

export default function FormEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ id: null, title: '', fields: [] });
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getForm(id)
      .then(setForm)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  function updateField(idx, patch) {
    setForm((f) => ({ ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x) }));
  }
  function addField() {
    setForm((f) => ({ ...f, fields: [...f.fields, newField()] }));
  }
  function removeField(idx) {
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
  }
  function moveField(idx, dir) {
    const next = [...form.fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setForm((f) => ({ ...f, fields: next }));
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const id = await saveForm(form);
      navigate(`/forms/${encodeURIComponent(id)}`);
      setForm((f) => ({ ...f, id }));
    } catch (e) { setError(e.message || String(e)); }
    setSaving(false);
  }

  if (loading) return <Page title="Yükleniyor…"><Card><Text>Form yükleniyor…</Text></Card></Page>;

  return (
    <Page
      title={id ? 'Formu Düzenle' : 'Yeni Form'}
      backAction={{ content: 'Formlar', onAction: () => navigate('/') }}
      primaryAction={{ content: saving ? 'Kaydediliyor…' : 'Kaydet', onAction: save, loading: saving }}
    >
      <BlockStack gap="400">
        {error && <Banner tone="critical" title="Hata">{error}</Banner>}

        <Card>
          <FormLayout>
            <TextField
              label="Form başlığı"
              value={form.title}
              onChange={(v) => setForm((f) => ({ ...f, title: v }))}
              autoComplete="off"
              helpText="Müşteriye gösterilen başlık (örn. 'Sipariş Formu')"
            />
          </FormLayout>
        </Card>

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h2">Alanlar ({form.fields.length})</Text>
              <Button onClick={addField}>+ Alan Ekle</Button>
            </InlineStack>
            {form.fields.length === 0 && (
              <Text tone="subdued">Henüz alan yok. "+ Alan Ekle" ile başla.</Text>
            )}
            {form.fields.map((field, idx) => (
              <FieldEditor
                key={idx}
                field={field}
                idx={idx}
                total={form.fields.length}
                allFields={form.fields}
                onChange={(patch) => updateField(idx, patch)}
                onRemove={() => removeField(idx)}
                onMove={(dir) => moveField(idx, dir)}
              />
            ))}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

function FieldEditor({ field, idx, total, allFields, onChange, onRemove, onMove }) {
  const meta = FIELD_TYPES.find((t) => t.value === field.type) || FIELD_TYPES[0];
  return (
    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <Text variant="headingSm">#{idx + 1} — {field.label || '(başlıksız)'}</Text>
          <InlineStack gap="200">
            <Button size="slim" onClick={() => onMove(-1)} disabled={idx === 0}>↑</Button>
            <Button size="slim" onClick={() => onMove(1)}  disabled={idx === total - 1}>↓</Button>
            <Button size="slim" tone="critical" onClick={onRemove}>Sil</Button>
          </InlineStack>
        </InlineStack>

        <FormLayout>
          <FormLayout.Group>
            <Select
              label="Tip"
              options={FIELD_TYPES.map((t) => ({ label: t.label, value: t.value }))}
              value={field.type}
              onChange={(v) => onChange({ type: v })}
            />
            <TextField
              label="Etiket (label)"
              value={field.label}
              onChange={(v) => onChange({ label: v })}
              autoComplete="off"
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Handle (kural referansı için)"
              value={field.handle}
              onChange={(v) => onChange({ handle: v })}
              autoComplete="off"
              helpText="Conditional rules bu handle ile alanı bulur"
            />
            <Checkbox
              label="Zorunlu"
              checked={field.required}
              onChange={(v) => onChange({ required: v })}
            />
          </FormLayout.Group>

          {meta.hasPlaceholder && (
            <TextField label="Placeholder" value={field.placeholder} onChange={(v) => onChange({ placeholder: v })} autoComplete="off" />
          )}
          {meta.hasMaxLength && (
            <TextField type="number" label="Max karakter" value={field.maxLength?.toString() || ''}
              onChange={(v) => onChange({ maxLength: parseInt(v) || null })} autoComplete="off" />
          )}
          {meta.hasAccept && (
            <TextField label="Kabul edilen dosya tipleri" value={field.accept} onChange={(v) => onChange({ accept: v })}
              autoComplete="off" placeholder=".jpg,.png,.pdf veya image/*" />
          )}
          {meta.hasContent && (
            <>
              <TextField label="Popup içeriği (HTML)" multiline={4} value={field.content} onChange={(v) => onChange({ content: v })} autoComplete="off" />
              <TextField label="Onay checkbox metni" value={field.confirmLabel} onChange={(v) => onChange({ confirmLabel: v })} autoComplete="off" />
            </>
          )}

          <TextField label="Yardım metni (alan altında görünür)" value={field.helpText} onChange={(v) => onChange({ helpText: v })} autoComplete="off" multiline={2} />

          {meta.hasOptions && (
            <OptionsEditor
              options={field.options}
              onChange={(options) => onChange({ options })}
            />
          )}

          <Divider />
          <RulesEditor
            rules={field.rules}
            allFields={allFields.filter((_, i) => i !== idx)}
            onChange={(rules) => onChange({ rules })}
          />
        </FormLayout>
      </BlockStack>
    </Box>
  );
}

function OptionsEditor({ options, onChange }) {
  return (
    <BlockStack gap="200">
      <Text variant="headingSm">Seçenekler ({options.length})</Text>
      {options.map((opt, i) => (
        <InlineStack key={i} gap="200" wrap={false} blockAlign="end">
          <Box width="100%">
            <FormLayout.Group>
              <TextField label="Etiket" value={opt.label} autoComplete="off"
                onChange={(v) => onChange(options.map((o, j) => j === i ? { ...o, label: v } : o))} />
              <TextField type="number" label="Ek ücret (₺)" value={String(opt.price)} autoComplete="off"
                onChange={(v) => onChange(options.map((o, j) => j === i ? { ...o, price: parseFloat(v) || 0 } : o))} />
            </FormLayout.Group>
          </Box>
          <Button size="slim" tone="critical"
            onClick={() => onChange(options.filter((_, j) => j !== i))}>
            ×
          </Button>
        </InlineStack>
      ))}
      <Button onClick={() => onChange([...options, newOption()])}>+ Seçenek Ekle</Button>
    </BlockStack>
  );
}

function RulesEditor({ rules, allFields, onChange }) {
  /* For now: a simple visual builder for ONE rule (most common case).
     Phase 3 will add multi-rule + AND/OR groups. */
  let parsed = { rules: [] };
  try { if (rules) parsed = JSON.parse(rules); } catch {}
  const r = parsed.rules?.[0] || null;

  function setRule(updates) {
    const next = { ...(r || { action: 'show', logic: 'all', conditions: [{ field: '', op: 'eq', value: '' }] }), ...updates };
    onChange(JSON.stringify({ rules: [next] }));
  }
  function clearRule() {
    onChange('');
  }

  return (
    <BlockStack gap="200">
      <Text variant="headingSm">Koşullu kural (opsiyonel)</Text>
      {!r ? (
        <Button onClick={() => setRule({})}>+ Kural Ekle</Button>
      ) : (
        <>
          <FormLayout.Group>
            <Select
              label="Davranış"
              value={r.action}
              options={[
                { label: 'Göster',     value: 'show' },
                { label: 'Gizle',      value: 'hide' },
                { label: 'Zorunlu yap',value: 'require' },
                { label: 'Zorunsuz yap',value: 'unrequire' },
              ]}
              onChange={(v) => setRule({ action: v })}
            />
            <Select
              label="Mantık"
              value={r.logic || 'all'}
              options={[{ label: 'Tüm koşullar', value: 'all' }, { label: 'Herhangi bir koşul', value: 'any' }]}
              onChange={(v) => setRule({ logic: v })}
            />
          </FormLayout.Group>

          {(r.conditions || []).map((c, ci) => (
            <FormLayout.Group key={ci}>
              <Select
                label="Alan"
                value={c.field}
                options={[{ label: '— seç —', value: '' }, ...allFields.map((f) => ({ label: `${f.label || f.handle} (${f.handle})`, value: f.handle }))]}
                onChange={(v) => setRule({ conditions: r.conditions.map((x, i) => i === ci ? { ...x, field: v } : x) })}
              />
              <Select
                label="Operatör"
                value={c.op}
                options={[
                  { label: '=', value: 'eq' }, { label: '≠', value: 'neq' },
                  { label: 'içerir (in)', value: 'in' }, { label: 'içermez', value: 'not_in' },
                  { label: 'metin içerir', value: 'contains' },
                  { label: 'boş', value: 'empty' }, { label: 'dolu', value: 'not_empty' },
                  { label: '>', value: 'gt' }, { label: '<', value: 'lt' },
                ]}
                onChange={(v) => setRule({ conditions: r.conditions.map((x, i) => i === ci ? { ...x, op: v } : x) })}
              />
              <TextField
                label="Değer"
                value={typeof c.value === 'string' ? c.value : JSON.stringify(c.value)}
                autoComplete="off"
                onChange={(v) => setRule({ conditions: r.conditions.map((x, i) => i === ci ? { ...x, value: v } : x) })}
                disabled={c.op === 'empty' || c.op === 'not_empty'}
              />
            </FormLayout.Group>
          ))}
          <InlineStack gap="200">
            <Button size="slim" onClick={() => setRule({ conditions: [...(r.conditions || []), { field: '', op: 'eq', value: '' }] })}>+ Koşul</Button>
            <Button size="slim" tone="critical" onClick={clearRule}>Kuralı Sil</Button>
          </InlineStack>
        </>
      )}
    </BlockStack>
  );
}
